/**
 * Every course's sessions in the person's own Google Calendar, in one press
 * (ADR 0040). The server shares each course calendar the person may read with
 * their school account; the browser then adds the ready ones to that
 * account's calendar list with a one-time token IMAM never sees.
 *
 * Drawn only where the school has a browser client and the person reads at
 * least one course calendar with a school account. On Today it steps aside once every course is in
 * (nothing left to do there); My sessions keeps it, with Google's own
 * page one press away. Nothing is stored about the person's Google list, so a
 * new course since the last press shows as new until it is added.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Bento } from "@/components/ui/box";
import type { FactBox, Read } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { googleCalendarApi, type MyCourseCalendars } from "@/lib/api/googleCalendar";
import { preloadGoogleCalendarSubscribe, subscribeCourseCalendars } from "@/lib/google-calendar-subscribe";
import { refusedRead } from "./course-page-boxes";
import { GOOGLE_CALENDAR_COPY, MY_CALENDARS_COPY, subscribeProblem } from "./google-calendar-copy";

const MY_CALENDARS_KEY = ["google-calendar", "mine"] as const;
const GOOGLE_CALENDAR_HOME = "https://calendar.google.com/calendar/r";

export function MyGoogleCalendars({ placement }: { placement: "today" | "sessions" }) {
  const copy = MY_CALENDARS_COPY;
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: MY_CALENDARS_KEY, queryFn: googleCalendarApi.mine, retry: false, staleTime: 0 });
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const subscribe = status.data?.subscribe ?? null;
  useEffect(() => { if (subscribe) preloadGoogleCalendarSubscribe(); }, [subscribe]);

  const addAll = async () => {
    if (!subscribe) return;
    setProblem(null);
    setNote(null);
    try {
      const outcome = await subscribeCourseCalendars(subscribe, async () => {
        const answer = await googleCalendarApi.shareMine();
        const { ready: _ready, pending: _pending, ...summary } = answer;
        queryClient.setQueryData(MY_CALENDARS_KEY, summary);
        return answer;
      });
      if (outcome.kind === "added") {
        const said = [copy.done(outcome.added)];
        if (outcome.pending > 0) said.push(copy.stillPreparing(outcome.pending));
        if (outcome.failed > 0) said.push(copy.notAll(outcome.failed));
        if (outcome.added > 0) setNote(said.join(" "));
        else setProblem(said.slice(1).join(" ") || GOOGLE_CALENDAR_COPY.notAdded);
      } else {
        setProblem(subscribeProblem(outcome, GOOGLE_CALENDAR_COPY.notAdded));
      }
    } catch (error) {
      if (refusedRead(error)) await queryClient.invalidateQueries({ queryKey: MY_CALENDARS_KEY });
      else setProblem(error instanceof ApiError && error.message ? error.message : GOOGLE_CALENDAR_COPY.notAdded);
    }
  };

  const readAt = new Date(status.dataUpdatedAt).toISOString();
  const read: Read<MyCourseCalendars> = refusedRead(status.error)
    ? { status: "forbidden" }
    : status.data
      ? status.isError
        ? { status: "stale", last: status.data, readAt, sentence: copy.stale, refresh: () => void status.refetch() }
        : { status: "ready", value: status.data, readAt }
      : status.isError
        ? { status: "failed", sentence: copy.failed, retry: () => void status.refetch() }
        : { status: "loading" };

  const value = read.status === "ready" ? read.value : read.status === "stale" ? read.last : null;
  // Offered only with a browser client and a course to read; a person without
  // a school account is told so on each course's own row.
  if (!value || value.state !== "available" || value.courses === 0 || !value.subscribe) return null;
  const missing = value.courses - value.added;
  // Today only asks for something to do; a just-finished press keeps its words.
  if (placement === "today" && missing <= 0 && !note && !problem) return null;

  const box: FactBox<MyCourseCalendars> = {
    kind: "fact",
    id: `my-google-calendars-${placement}`,
    label: copy.label,
    job: copy.job,
    owner: { system: "imam" },
    fold: "never",
    read,
    prose: (v) => v.added === 0
      ? copy.available(v.courses)
      : v.courses - v.added > 0
        ? copy.someNew(v.courses - v.added)
        : copy.allAdded(v.courses),
    detail: () => copy.detail,
    act: missing > 0
      ? { does: "leave", verb: value.added === 0 ? copy.addAll : copy.addNew(missing), run: addAll }
      : { does: "leave", verb: copy.open, providerUrl: GOOGLE_CALENDAR_HOME },
  };

  return <div className="grid gap-1">
    <Bento id={`my-google-calendars-${placement}`} journey="course-calendar" boxes={[box]} />
    {problem && <p role="alert" className="text-ui">{problem}</p>}
    {!problem && note && <p role="status" className="text-ui">{note}</p>}
  </div>;
}
